import { useEffect, useRef, useState } from 'react'
import { cn } from '../../lib/cn.js'
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, ChevronsUpDown } from 'lucide-react'
import { type MenuItem } from '../ui/DropdownMenu.js'
import { OpenLink } from '../ui/OpenLink.js'
import { RowMenu } from './RowMenu.js'
import { Text } from '../ui/Typography.js'
import { Row } from '../layout/Stack.js'
import { Button } from '../ui/Button.js'
import { Skeleton } from '../ui/Skeleton.js'
import { EmptyState } from './EmptyState.js'
import { ErrorState } from '../feedback/ErrorState.js'

/** Which column, and which way. `null` is the list's own default order. */
export interface SortBy {
  key: string
  direction: 'asc' | 'desc'
}

export interface Column<T> {
  key: string
  header: React.ReactNode
  /** The cell. Given the whole row, because most cells need more than one field. */
  cell: (row: T) => React.ReactNode
  /** Right-aligned and tabular, for money and counts. */
  numeric?: boolean
  /** Hidden below this width. The desk is wide, but not on every machine. */
  hideBelow?: 'sm' | 'md' | 'lg'
  width?: string
  /**
   * What the server calls this column, if it can be ordered by.
   *
   * A column without one is a heading. The key is the server's name rather
   * than the column's, because the sort happens there — the screen does not
   * get to invent an order the route cannot honour.
   */
  sortKey?: string
}

export interface DataTableProps<T> {
  columns: Column<T>[]
  rows: T[] | undefined
  rowKey: (row: T) => string
  loading?: boolean
  /** What to draw when there are no rows. Required — an empty table is not an answer. */
  empty: React.ReactNode
  /**
   * What this table is, when it is empty.
   *
   * Every table with a string `empty` was headed **"Nothing here"** — which
   * contradicts the rule written on `EmptyState` itself: an empty list *"names
   * itself rather than saying today on all three"*. Nine tables said the same
   * three words.
   */
  emptyTitle?: string
  /** A drawing from the brand's set. See `EmptyState`. */
  emptyIllustration?: string
  /**
   * The query failed.
   *
   * **A table that could not load is not a table with nothing in it**, and
   * without this it drew the empty state — so a Settings tab whose request
   * had failed said *"No cities yet"*. That is not unhelpful, it is untrue:
   * it tells somebody the platform has no cities, and the obvious next act is
   * to add one that already exists.
   *
   * Twelve screens were doing it. The distinction lives here rather than in
   * each of them, so a new table cannot forget it — it either passes this or
   * it is a table whose failure is silent, and that is now visible at the
   * call site.
   */
  error?: boolean
  onRetry?: () => void
  /**
   * The order the rows are in, and how to change it.
   *
   * **No table on this desk could be sorted.** Every list came back in one
   * order the server chose — providers newest first, bookings by when they
   * start — and the screen neither said so nor let anybody change it, which
   * on a table of fifty names is the difference between finding somebody and
   * scrolling.
   *
   * Server-side, deliberately. A table that sorts the fifty rows it happens
   * to hold is a table that lies: the first name alphabetically among the
   * fifty most recent is not the first name alphabetically.
   *
   * A column opts in with `sortKey`. Anything without one stays a heading.
   */
  sort?: SortBy | null
  onSort?: (next: SortBy | null) => void
  /**
   * Which page, counting from zero, and how to change it.
   *
   * **There was no way past the first fifty.** The footer said "narrow it
   * with a filter or the search to see the rest", which is a real
   * instruction only if you can guess a term that isolates what you want —
   * and useless for "who signed up in March", where the answer is simply
   * further down a list.
   *
   * Offset rather than a cursor, because this desk sorts: a cursor has to
   * encode the column it is a cursor *in*, and "page 3 by bookings
   * descending" is not something a `created_at` cursor can express.
   *
   * The screen resets this to zero whenever a filter, a search or a sort
   * changes — being left on page 4 of a list that now has one page is a
   * table that looks empty.
   */
  page?: number
  onPage?: (next: number) => void
  /**
   * Makes a row openable.
   *
   * It draws a real `<button>` in a trailing cell — which is what the boards
   * draw (**Open** on five tables, **Edit** on events) and the only version a
   * keyboard can reach. The whole row stays clickable with the mouse as a
   * convenience on top of that, never instead of it.
   *
   * This used to be an `onClick` on the `<tr>` and nothing else, directly under
   * a comment claiming it was "a real button for a keyboard rather than an
   * onClick on a `<tr>`". Every table on the desk was unreachable without a
   * mouse, and the comment said the opposite of its own code.
   */
  onRowClick?: (row: T) => void
  /** What that button says. **Open** everywhere except the events table. */
  rowActionLabel?: string
  /**
   * The actions on a row, behind one control.
   *
   * Asked for directly, looking at the running desk: *"action buttons are text
   * buttons, should have been a three-dot menu with popover menu items"*. Every
   * table had its own trailing column of bare text links — "Refund", "Remove",
   * "Try again", "Open" — in a different order and a different width on each
   * screen, and a row with two of them read as a sentence rather than as
   * controls.
   *
   * Opening the row is added to the top of the menu automatically when
   * `onRowClick` is set, so no screen lists it.
   */
  rowActions?: (row: T) => MenuItem[]
  className?: string
  /**
   * Take the height that is left, and scroll the **rows** rather than the page.
   *
   * For a full-page list. A table inside a record or a dialog leaves it off:
   * that is a short list in a page that scrolls as a whole, and pinning it
   * would put a scrollbar inside a scrollbar.
   */
  fill?: boolean
  /** How many skeleton rows to draw while loading. Match the usual page size. */
  loadingRows?: number
  /**
   * The page size that was asked for, so a full page can say so.
   *
   * The boards end every table with *"Showing 5 of 5"*. No list endpoint returns
   * a total, so this says what is true instead of inventing one: the count, and
   * — when the count is exactly the limit — that there may be more behind it.
   */
  /**
   * How many rows to draw, when the query asked for **one more than this**.
   *
   * The extra row is the whole mechanism: a table that asks for 25 and
   * receives 25 cannot tell a full page from a complete list, so the footer
   * said *"Showing the first 25. Narrow it with a filter or the search to
   * see the rest"* to a list of exactly 25 — telling somebody to go looking
   * for rows that do not exist. Asking for 26 and drawing 25 answers it.
   */
  pageSize?: number
  /**
   * How many there are altogether, matching whatever is filtered and searched.
   *
   * The figure the screen already draws in its strip. Passed here so the
   * footer can say "of 1,203" rather than leaving somebody to guess how far
   * through a list they are. Leave it out and the footer says what it always
   * said — a wrong total is worse than none.
   */
  total?: number
}

/**
 * Every table on the desk.
 *
 * Three things it does that a hand-built `<table>` keeps not doing: it draws the
 * **shape** while loading rather than a spinner, it refuses to render an empty grid
 * of headings, and a clickable row is a real button for a keyboard rather than an
 * `onClick` on a `<tr>`.
 */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  loading,
  empty,
  emptyTitle,
  emptyIllustration = 'records',
  error,
  onRetry,
  sort,
  onSort,
  page,
  onPage,
  fill,
  onRowClick,
  rowActions,
  rowActionLabel = 'Open',
  className,
  loadingRows = 6,
  pageSize,
  total,
}: DataTableProps<T>) {
  /**
   * The trailing action column, added rather than asked for at every call site.
   *
   * One ⋮ per row rather than a line of text links. `stopPropagation` on the
   * cell as well as the trigger, because the row itself is clickable and a
   * menu that also opens the record behind it is a menu nobody trusts.
   */
  const scroller = useRef<HTMLDivElement | null>(null)
  const room = useSidewaysRoom(scroller, rows)

  const extrasFor = (row: T): MenuItem[] => (rowActions ? rowActions(row) : [])
  const menuFor = (row: T): MenuItem[] => [
    ...(onRowClick ? [{ label: rowActionLabel, onSelect: () => onRowClick(row) }] : []),
    ...extrasFor(row),
  ]

  /**
   * No ⋮ where the only thing in it is **Open**.
   *
   * Asked for in those words: *"whenever there is open by row click and three
   * dot menu also have only the open option there should not be any three dot
   * menu at all, that is just same functionality at same place twice … if
   * there are other options other than opening it then it's fine"*.
   *
   * Four tables were exactly that — a menu you press to reveal one item that
   * does what pressing the row already did. Decided **per row** rather than
   * per table, because `rowActions` is a function of the row: a class that is
   * finished offers nothing but opening, while a draft beside it offers three
   * things, and the finished one should not carry an empty control to keep
   * the column tidy.
   *
   * The column itself goes when no row on the page has anything extra.
   */
  const anyExtras = (rows ?? []).some((row) => extrasFor(row).length > 0)
  const shown: Column<T>[] = anyExtras
    ? [
        ...columns,
        {
          key: '__actions',
          /* Named for a screen reader, blank on screen. An empty `th` is a
             column somebody navigating by heading cannot identify — they
             hear "column six" and nothing else. */
          header: <span className="sr-only">What you can do</span>,
          width: '1%',
          cell: (row) => (extrasFor(row).length > 0 ? <RowMenu items={menuFor(row)} /> : null),
        },
      ]
    : columns
  const hide = (c: Column<T>) =>
    c.hideBelow === 'sm'
      ? 'hidden sm:table-cell'
      : c.hideBelow === 'md'
        ? 'hidden md:table-cell'
        : c.hideBelow === 'lg'
          ? 'hidden lg:table-cell'
          : ''

  /**
   * One more row than is drawn means there are more behind it.
   *
   * The screens ask for `pageSize + 1`; this draws `pageSize` and uses the
   * extra only to know whether to say so.
   */
  const more = pageSize !== undefined && rows !== undefined && rows.length > pageSize
  const shownRows = more ? rows!.slice(0, pageSize) : rows

  /**
   * Which rows these are, of what.
   *
   * This said *"Showing 51–100"* and nothing else, under a comment reading
   * *"No total: none of the list routes counts"* — which stopped being true
   * when every list screen grew a figure strip. The first figure on all seven
   * of them is `totals.matched`, the count of everything the filters and the
   * search box match, worked out by the same `where` clause as the rows.
   *
   * Without it there is no way to tell the second page of three from the
   * second page of forty. "Next" tells you there is more; it does not tell
   * you whether you are nearly there, which is the thing somebody wants to
   * know before they start pressing it.
   *
   * It is still only said when a screen passes it. A number invented from a
   * page of fifty would be a number somebody quotes.
   */
  const from = (page ?? 0) * (pageSize ?? 0) + 1
  const of = total === undefined ? '' : ` of ${total.toLocaleString()}`
  const countSaid =
    shownRows === undefined
      ? ''
      : page !== undefined && pageSize !== undefined && (page > 0 || more)
        ? `Showing ${from}–${from + shownRows.length - 1}${of}`
        : more
          ? `Showing the first ${pageSize}${of}. There are more. Narrow it with a filter or the search to see them.`
          : `Showing ${shownRows.length} of ${shownRows.length}`

  /* A `fill` table owns the height that is left, and so must whatever it
     draws instead of rows — otherwise "nothing matched" sits squashed at the
     top of an empty screen. */
  const instead = (node: React.ReactNode) =>
    fill ? <div className="lg:min-h-0 lg:flex-1 lg:overflow-auto">{node}</div> : <>{node}</>

  /* Before the empty state, because "nothing here" and "this did not load"
     are different answers and only one of them is true. */
  if (!loading && error) {
    return instead(<ErrorState {...(onRetry ? { onRetry } : {})} />)
  }

  if (!loading && shownRows && shownRows.length === 0) {
    return instead(
      typeof empty === 'string' ? (
        <EmptyState
          title={emptyTitle ?? 'Nothing here'}
          description={empty}
          {...(emptyIllustration ? { illustration: emptyIllustration } : {})}
        />
      ) : (
        <>{empty}</>
      ),
    )
  }

  return (
    <div
      className={cn(
        /**
         * `relative`, and it is not decoration.
         *
         * The actions column's heading is a `sr-only` span — *"What you can
         * do"* — and Tailwind's `sr-only` is `position: absolute`. An
         * absolutely positioned box is contained by its nearest **positioned**
         * ancestor, not by the nearest one with `overflow`, so in a `static`
         * scroller it escapes the scroller completely: it laid itself out at
         * the table's full 601px inside a 354px column and pushed the whole
         * **page** 178px wide on a phone.
         *
         * Nothing else could stop it. `overflow-x: hidden` on `main`, on the
         * page body and on this wrapper all left the page scrolling, because
         * clipping only applies to descendants an element actually contains.
         * One word here does what none of those could.
         */
        'border-hairline bg-surface relative rounded-lg border',
        /**
         * The **rows** scroll, not the page.
         *
         * Said plainly, using it: *"members table have really long list and to
         * go to next page i have to scroll so long to the button to click next
         * such bad ux and the whole page scrolling instead of table rows"*.
         *
         * Fifty rows is about 2,800px, so Next sat two and a half screens
         * below the fold — and getting back to the filters meant scrolling all
         * the way up again. With `fill` the table takes the height that is
         * left, its head stays put, its footer stays put, and the only thing
         * that moves is the rows.
         *
         * Off by default: a table inside a record or a dialog is a short list
         * in a page that scrolls as a whole, and pinning it there would put a
         * scrollbar inside a scrollbar.
         */
        /* A filling table pins its own head and footer — on a laptop. Below
           `lg` the page scrolls as one thing (see `PageBody`), so the table is
           an ordinary block that happens to scroll sideways. */
        fill
          ? 'overflow-x-auto lg:flex lg:min-h-0 lg:flex-1 lg:flex-col lg:overflow-hidden'
          : 'overflow-x-auto',
        className,
      )}
      ref={scroller}
    >
      {/**
       * The edge that says there is more.
       *
       * A phone has no scrollbar, so a table cut off at the right looked
       * like a table with two columns. The fade is drawn **only while there
       * is something behind it** and goes the moment the last column is
       * reached, so it is a fact about the table rather than decoration.
       *
       * `sticky`, not `absolute`: an absolutely positioned child of a scroll
       * container scrolls away with the content, which would put the hint
       * somewhere off to the right where nobody sees it. Sticky pins it to
       * the visible edge. Zero width so it takes no space in the row, and
       * `pointer-events-none` so it never swallows a tap on the cell under
       * it.
       */}
      {room.right && (
        <div
          aria-hidden
          className="from-surface pointer-events-none sticky right-0 z-20 float-right h-full w-6 bg-gradient-to-l to-transparent lg:hidden"
        />
      )}
      <div
        className={cn(
          fill ? 'contents lg:block lg:min-h-0 lg:flex-1 lg:overflow-auto' : 'contents',
        )}
      >
        <table className="w-full border-collapse text-body">
          <thead className={cn(fill && 'bg-surface lg:sticky lg:top-0 lg:z-10')}>
            <tr className="border-b border-hairline">
              {shown.map((c) => {
                const active = c.sortKey !== undefined && sort?.key === c.sortKey
                const cell = (
                  <th
                    key={c.key}
                    scope="col"
                    aria-sort={
                      active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : undefined
                    }
                    className={cn(
                      'text-micro text-ink-muted px-4 py-3 text-left font-medium uppercase tracking-wide',
                      c.numeric && 'text-right',
                      hide(c),
                    )}
                    {...(c.width ? { style: { width: c.width } } : {})}
                  >
                    {c.sortKey !== undefined && onSort ? (
                      <button
                        type="button"
                        onClick={() => {
                          /* Ascending, then descending, then back to whatever
                           order the list has of its own — three states, so
                           there is a way out of a sort rather than only a
                           way between two. */
                          if (!active) return onSort({ key: c.sortKey!, direction: 'asc' })
                          if (sort.direction === 'asc') {
                            return onSort({ key: c.sortKey!, direction: 'desc' })
                          }
                          return onSort(null)
                        }}
                        className={cn(
                          /**
                           * Tall enough to tap on a phone.
                           *
                           * `py-0.5` made this 21px high. A finger is nearer
                           * forty, and the column heading is one of the few
                           * controls on a list a thumb actually reaches for.
                           * The row height does not change — the cell's own
                           * `py-3` already reserves more than this — so the
                           * only thing that grows is the target.
                           */
                          'min-h-9 lg:min-h-0',
                          'group -mx-1 inline-flex items-center gap-1 rounded-sm px-1 py-0.5 outline-none',
                          'hover:text-ink focus-visible:ring-coral-fill/30 focus-visible:ring-2',
                          'text-micro font-medium uppercase tracking-wide',
                          active && 'text-ink',
                          c.numeric && 'flex-row-reverse',
                        )}
                      >
                        {c.header}
                        {active ? (
                          sort.direction === 'asc' ? (
                            <ArrowUp className="size-3" aria-hidden />
                          ) : (
                            <ArrowDown className="size-3" aria-hidden />
                          )
                        ) : (
                          /* Shown on hover only: an arrow on every sortable
                           heading is a row of arrows, and a heading with no
                           hint at all is a button nobody finds. */
                          <ChevronsUpDown
                            className="size-3 opacity-0 transition-opacity group-hover:opacity-60"
                            aria-hidden
                          />
                        )}
                      </button>
                    ) : (
                      c.header
                    )}
                  </th>
                )
                return cell
              })}
            </tr>
          </thead>
          <tbody>
            {loading || !shownRows
              ? Array.from({ length: loadingRows }, (_, i) => (
                  <tr key={i} className="border-b border-hairline last:border-0">
                    {shown.map((c) => (
                      <td key={c.key} className={cn('px-4 py-3', hide(c))}>
                        <Skeleton className="h-4 w-full max-w-[12rem]" />
                      </td>
                    ))}
                  </tr>
                ))
              : shownRows.map((row) => (
                  <tr
                    key={rowKey(row)}
                    className={cn(
                      'border-b border-hairline last:border-0',
                      onRowClick &&
                        'cursor-pointer transition-colors hover:bg-sunk focus-visible:bg-sunk focus-visible:outline-coral-fill focus-visible:outline-2 focus-visible:-outline-offset-2',
                    )}
                    /**
                     * A keyboard can reach the row itself.
                     *
                     * This mattered the moment the trailing ⋮ stopped being
                     * drawn on tables whose only action was opening: that
                     * button was the **only** thing a keyboard could reach,
                     * and removing it without this would have put every one of
                     * those tables back to mouse-only — which is the exact
                     * defect `onRowClick`'s own note records being fixed once
                     * already.
                     *
                     * `tabIndex` and a key handler rather than wrapping a cell
                     * in a button: the first column is whatever a screen puts
                     * there — a name, an avatar and two lines, a status chip —
                     * and a component cannot wrap arbitrary content in a
                     * button without breaking someone's layout.
                     *
                     * Space is prevented, or the page scrolls underneath the
                     * record that just opened.
                     */
                    {...(onRowClick
                      ? {
                          tabIndex: 0,
                          onKeyDown: (e: React.KeyboardEvent) => {
                            if (e.target !== e.currentTarget) return
                            if (e.key !== 'Enter' && e.key !== ' ') return
                            e.preventDefault()
                            onRowClick(row)
                          },
                        }
                      : {})}
                    /**
                     * The row opens the record — unless the click landed on a
                     * control inside it.
                     *
                     * This fired on **every** click in the row, including the
                     * ones on its own action buttons, so a cell button ran and
                     * was then immediately overridden by the row. On 9.6 that
                     * meant *"Prepare the room"* opened the class **editor**:
                     * the console was set and the row's own handler replaced it
                     * a moment later. The only reason the trailing Open button
                     * worked is that it calls `stopPropagation` by hand, which
                     * is a fix one call site remembered and no other could.
                     *
                     * Checked here rather than in every cell, because the rule
                     * is about tables and not about any one button.
                     */
                    onClick={
                      onRowClick
                        ? (e) => {
                            const target = e.target as HTMLElement
                            if (
                              target.closest('button, a, input, select, textarea, [role="button"]')
                            )
                              return
                            onRowClick(row)
                          }
                        : undefined
                    }
                  >
                    {shown.map((c) => (
                      <td
                        key={c.key}
                        className={cn(
                          'px-4 py-3 align-middle',
                          /**
                           * A floor under a column's width, on a phone only.
                           *
                           * A table narrower than its content shares the
                           * squeeze out among the columns, and the one
                           * holding a sentence loses: *"Getting your money in
                           * order after a separation"* came out as six lines
                           * of one or two words and made the row six rows
                           * tall. The table already scrolls sideways — it
                           * should use that rather than stack words
                           * vertically.
                           *
                           * Not on numbers, dates or the actions column:
                           * those are short by nature and a floor under them
                           * would push the readable columns off the screen
                           * for nothing.
                           */
                          !c.numeric && c.key !== '__actions' && 'min-w-[7.5rem] lg:min-w-0',
                          c.numeric && 'text-right tabular-nums',
                          hide(c),
                        )}
                      >
                        {c.cell(row)}
                      </td>
                    ))}
                  </tr>
                ))}
          </tbody>
        </table>
      </div>
      {!loading && shownRows && (
        <div className="border-hairline flex items-center justify-between gap-4 border-t px-4 py-2">
          <Text variant="caption">{countSaid}</Text>
          {/* Back and Next only where there is somewhere to go. On a list
              that fits one page they would be two disabled buttons saying
              nothing. */}
          {onPage !== undefined && page !== undefined && (page > 0 || more) && (
            <Row gap={2}>
              <Button
                variant="secondary"
                size="sm"
                leadingIcon={<ChevronLeft />}
                disabled={page === 0}
                onClick={() => onPage(page - 1)}
              >
                Back
              </Button>
              <Button
                variant="secondary"
                size="sm"
                trailingIcon={<ChevronRight />}
                disabled={!more}
                onClick={() => onPage(page + 1)}
              >
                Next
              </Button>
            </Row>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * The two-line cell every table uses for a person: a name, and what
 * identifies them.
 *
 * `onOpen` draws the name as the thing that opens the row.
 *
 * It became necessary the moment the trailing ⋮ stopped being drawn on tables
 * whose only action was opening: that menu was the only thing on the row
 * saying the row did anything. The lesson is already recorded on the overview
 * panel — *"a name that opens a record, drawn in the same ink as every other
 * word on the page, gives no sign it can be pressed until the cursor happens
 * to land on it"* — and `OpenLink` is the desk's one treatment for it.
 */
/**
 * Whether a sideways scroller has more to the right, and whether it has moved.
 *
 * A table narrower than its columns simply cuts them off. On a laptop the
 * scrollbar says so; on a phone there is no scrollbar and no hint at all, so
 * Members showed *Member* and *City* and gave no sign that *Status* and
 * *Joined* existed. Somebody reads the two columns they can see and concludes
 * that is the table.
 *
 * Measured rather than assumed, and re-measured on resize and on content
 * changing: a table that fits after a filter is applied must stop hinting.
 */
function useSidewaysRoom(ref: React.RefObject<HTMLDivElement | null>, rows: unknown) {
  const [room, setRoom] = useState({ right: false, left: false })
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const check = () => {
      const slack = el.scrollWidth - el.clientWidth
      setRoom({
        right: slack > 2 && el.scrollLeft < slack - 2,
        left: slack > 2 && el.scrollLeft > 2,
      })
    }
    check()
    el.addEventListener('scroll', check, { passive: true })
    const watcher = new ResizeObserver(check)
    watcher.observe(el)
    return () => {
      el.removeEventListener('scroll', check)
      watcher.disconnect()
    }
  }, [ref, rows])
  return room
}

export function PrimaryCell({
  title,
  subtitle,
  onOpen,
}: {
  title: React.ReactNode
  subtitle?: React.ReactNode
  onOpen?: () => void
}) {
  return (
    <div className="flex flex-col gap-0.5">
      {onOpen ? (
        <OpenLink onClick={onOpen}>{title}</OpenLink>
      ) : (
        <Text as="span" variant="body-strong">
          {title}
        </Text>
      )}
      {subtitle && (
        <Text as="span" variant="caption">
          {subtitle}
        </Text>
      )}
    </div>
  )
}
